import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  PageState,
  PageStateService,
} from '../../../services/page-state/page-state.service';
import { Observable } from 'rxjs';
import { IconsModule } from '../../../icons/icons.module';
import { PageHeaderService } from '../../../services/page-header/page-header.service';

@Component({
  selector: 'zikatagov-page-header',
  standalone: true,
  imports: [CommonModule, IconsModule],
  template: `
    <header class="page-header">
      <div class="header-inner">

        <!-- Left: Title + Breadcrumb -->
        <div class="header-left">
          <div class="title-block">
            <span class="title-accent"></span>
            <h1 class="page-title">{{ (pageState$ | async)?.title }}</h1>
          </div>

          <nav class="breadcrumb-nav" aria-label="breadcrumb">
            <ol class="breadcrumb-list">
              <ng-container
                *ngFor="
                  let item of (pageState$ | async)?.breadcrumb;
                  let last = last;
                  let first = first
                "
              >
                <li class="breadcrumb-item" [class.breadcrumb-item--active]="last">
                  <span class="breadcrumb-sep" *ngIf="!first" aria-hidden="true">/</span>
                  <span class="breadcrumb-text">{{ item }}</span>
                </li>
              </ng-container>
            </ol>
          </nav>
        </div>

        <!-- Right: Slot -->
        <div class="header-right" *ngIf="(rightContent$ | async) as tmpl">
          <ng-container *ngTemplateOutlet="tmpl"></ng-container>
        </div>

      </div>

      <div class="header-rule"></div>
    </header>
  `,
  styles: [`
    :host {
      display: block;
      font-family: 'DM Sans', 'Segoe UI', system-ui, sans-serif;
    }

    /* ── Shell ── */
    .page-header {
      position: sticky;
      top: 0;
      z-index: 100;
      background: rgba(255, 255, 255, 0.92);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      padding: 0 clamp(1rem, 4vw, 2rem);
      box-shadow: 0 1px 0 0 rgba(109, 74, 255, 0.08);
    }

    /* ── Inner layout ── */
    .header-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      min-height: 60px;
      padding: 0.75rem 0;
    }

    /* ── Left ── */
    .header-left {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }

    /* ── Title ── */
    .title-block {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      flex-shrink: 0;
    }

    .title-accent {
      display: inline-block;
      width: 3px;
      height: 20px;
      border-radius: 99px;
      background: linear-gradient(180deg, #6d4aff 0%, #a78bfa 100%);
      flex-shrink: 0;
    }

    .page-title {
      margin: 0;
      font-size: clamp(0.95rem, 2.5vw, 1.125rem);
      font-weight: 700;
      letter-spacing: -0.025em;
      color: #0f0a1e;
      white-space: nowrap;
      line-height: 1;
    }

    /* ── Divider ── */
    .header-left::after {
      content: '';
      display: block;
      width: 1px;
      height: 16px;
      background: #ddd8f0;
      flex-shrink: 0;
    }

    /* ── Breadcrumb ── */
    .breadcrumb-nav {
      min-width: 0;
      overflow: hidden;
    }

    .breadcrumb-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      align-items: center;
      gap: 0.25rem;
      flex-wrap: nowrap;
      overflow: hidden;
    }

    .breadcrumb-item {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      min-width: 0;
    }

    /* Hide all but last two breadcrumb items on small screens */
    @media (max-width: 480px) {
      .breadcrumb-item:not(.breadcrumb-item--active):not(:nth-last-child(2)) {
        display: none;
      }

      .breadcrumb-item:nth-last-child(2) .breadcrumb-sep::before {
        content: '…';
        margin-right: 0.25rem;
      }
    }

    .breadcrumb-sep {
      font-size: 0.75rem;
      color: #c4bfda;
      user-select: none;
      flex-shrink: 0;
    }

    .breadcrumb-text {
      font-size: 0.8125rem;
      font-weight: 400;
      color: #7c6fa0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: color 0.15s ease;
      max-width: 160px;
    }

    .breadcrumb-item:not(.breadcrumb-item--active):hover .breadcrumb-text {
      color: #6d4aff;
      cursor: pointer;
    }

    .breadcrumb-item--active .breadcrumb-text {
      color: #1a1030;
      font-weight: 600;
      max-width: 200px;
    }

    /* ── Right slot ── */
    .header-right {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    /* ── Bottom rule ── */
    .header-rule {
      height: 1px;
      background: linear-gradient(
        90deg,
        rgba(109, 74, 255, 0.15) 0%,
        rgba(167, 139, 250, 0.1) 40%,
        transparent 100%
      );
    }

    /* ── Tablet: tighten up ── */
    @media (max-width: 768px) {
      .header-inner {
        min-height: 52px;
        gap: 0.75rem;
      }

      .title-accent {
        height: 18px;
      }

      .breadcrumb-text {
        max-width: 120px;
      }

      .breadcrumb-item--active .breadcrumb-text {
        max-width: 140px;
      }
    }

    /* ── Mobile: compact single-line ── */
    @media (max-width: 480px) {
      .header-inner {
        min-height: 48px;
        padding: 0.625rem 0;
      }

      .breadcrumb-text {
        font-size: 0.75rem;
        max-width: 90px;
      }

      .breadcrumb-item--active .breadcrumb-text {
        max-width: 110px;
      }
    }
  `],
})
export class ZikatagPageHeaderComponent {
  private pageStateService = inject(PageStateService);
  private pageHeaderService = inject(PageHeaderService);

  pageState$: Observable<PageState> = this.pageStateService.getPageState();
  rightContent$ = this.pageHeaderService.rightContent$;
}